import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ShieldCheck, Award, Clock, Wrench, Globe, FlaskConical } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const reasons = [
  {
    icon: ShieldCheck,
    title: "Dual OEM Compliance",
    desc: "One of very few Indian products certified to both Airbus AIMS 09-00-002 and Boeing BSS 7432 simultaneously.",
    cert: "Airbus + Boeing",
  },
  {
    icon: Award,
    title: "ISO Certified Quality",
    desc: "ISO 9001:2015 Quality Management and ISO 14001:2015 Environmental Management certified manufacturing.",
    cert: "ISO 9001 + 14001",
  },
  // {
  //   icon: FlaskConical,
  //   title: "Independent Testing",
  //   desc: "All products independently tested by Scientific Material International (SMI Inc.), Miami, USA.",
  //   cert: "SMI Inc. Verified",
  // },
  {
    icon: Clock,
    title: "Fast Turnaround",
    desc: "Domestic manufacturing means days vs. weeks. No import delays, customs holds, or supply chain disruptions.",
    cert: "Made in India",
  },
  {
    icon: Globe,
    title: "Indian Conditions",
    desc: "Formulated specifically for Indian water hardness variability and operating conditions across all stations.",
    cert: "Localized R&D",
  },
  {
    icon: Wrench,
    title: "Direct Technical Access",
    desc: "Same-timezone, same-day technical support directly from our formulation team in Bengaluru.",
    cert: "Bengaluru HQ",
  },
];

const certifications = [
  { code: "Boeing BSS 7432", tests: ["Sandwich Corrosion", "Acrylic Crazing", "Paint Softening", "Hydrogen Embrittlement"], status: "CONFORMS" },
  { code: "Airbus AIMS 09-00-002", tests: ["Sandwich Corrosion", "Total Immersion", "Hydrogen Embrittlement", "Paint Softening", "Acrylic Crazing", "Polycarbonate Crazing"], status: "CONFORMS" },
];

export default function WhyChooseUs() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="why-us"
      data-testid="why-choose-us-section"
      className="py-24 sm:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Header */}
        <motion.div
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          className="text-center mb-16"
        >
          <motion.p variants={fadeUp} className="text-sm uppercase tracking-[0.2em] font-medium text-[#0077D1] mb-4">
            Why OZO AeroCare
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.2] font-semibold text-[#0C3A72] mb-4">
            The Indigenous Advantage
          </motion.h2>
          <motion.div variants={fadeUp} className="section-divider mx-auto" />
        </motion.div>

        {/* Reasons Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {reasons.map((item, idx) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                data-testid={`why-choose-card-${idx}`}
                className="group bg-[#F7F4EC] border border-[#0C3A72]/10 p-8 hover:border-[#0077D1]/30 hover:shadow-md transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[#0077D1]/10 flex items-center justify-center mb-5 group-hover:bg-[#0077D1]/20 transition-colors">
                  <Icon className="w-6 h-6 text-[#0077D1]" />
                </div>
                <h3 className="text-xl font-medium text-[#0C3A72] mb-3">{item.title}</h3>
                <p className="text-sm text-[#4A627E] leading-relaxed mb-4">{item.desc}</p>
                <span className="cert-badge text-xs tracking-wider text-[#0077D1] border border-[#0077D1]/20 px-2 py-1">
                  {item.cert}
                </span>
              </motion.div>
            );
          })}
        </div>

        {/* Certifications Detail */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-8">
            <p className="text-sm uppercase tracking-[0.2em] font-medium text-[#0077D1]">Test Results of OZO AeroCare ST-10 Aircraft exterior cleaner
              
            </p>
            <div className="h-px flex-1 bg-[#0C3A72]/10" />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {certifications.map((cert) => (
              <div
                key={cert.code}
                data-testid={`certification-${cert.code.split(' ')[0].toLowerCase()}`}
                className="bg-[#F7F4EC] border border-[#0C3A72]/10 p-8"
              >
                <h4 className="cert-badge text-lg font-semibold text-[#0C3A72] mb-1">{cert.code}</h4>
                {/* <p className="text-sm text-[#4A627E] mb-6">Tested by SMI Inc., USA — January 2026</p> */}
                <div className="space-y-3">
                  {cert.tests.map((test) => (
                    <div key={test} className="flex items-center justify-between py-2 border-b border-[#0C3A72]/5 last:border-0">
                      <span className="text-sm text-[#0C3A72]">{test}</span>
                      <span className="cert-badge text-xs tracking-wider text-emerald-600 bg-emerald-50 px-2 py-0.5">
                        {cert.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
